import * as React from 'react';
import { Modal, Row, Col, Button, Dropdown } from 'antd';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { TextView } from 'src/core/ui/text.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { DateView } from 'src/core/ui/date.view';
import GatewayTabs from 'src/app/accounting/payment-gateway/gateway-tabs';
import { CurrencyEditorController } from 'src/app/general/currencies/parts/currency-editor.controller';
import { BooleanView } from 'src/core/ui/boolean.view';
import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { BillingService } from 'src/app/accounting/shared/billing.service';

interface Props {
	bookingDate: Date;
	serviceDate: Date;
	defaultCurrencyId: number;
	defaultCurrencyCode: string;
	quoteId: number;
	accountingTarget: number;
	editableAmount: boolean;
	amount?: number;
	minAmount?: number;
	maxAmount?: number;
	transactionId?: string;
	disableCreditCardEntry?: boolean;
	title?: string;
	target: TargetPayment;
	setBusy: (isBusy: boolean) => void;
	onSubmitPayment: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => Promise<void>;
	onCancelDialog: () => void;
}

interface TargetPayment {
	options: TargetModel[];
	render?: (selected: TargetModel) => React.ReactNode;
}

interface State {
	amount: number;
	cardToken: string;
	serviceName: string;
	paymentDate: Date;
	method: number;
	paymentReference: string;
	currencyId: number;
	currencyRate: number;
	selectedTarget: TargetModel;
	selectedTargetBillingAddresses?: Address[];
	selectedTargetBillingAddress?: Address;
	otherProps: { [prop: string]: string };
	immediatelyPerformPayment: boolean;

	currencyCode: string;
}

export interface TargetModel {
	id: number;
	displayName: string;
	firstName: string;
	lastName: string;
}

@observer
export class PaymentModal extends React.Component<Props, State> {
	@resolve
	private contextService: ContextService;

	@resolve
	private toast: ToastNotifications;

	@resolve
	private billingService: BillingService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			amount: props.amount,
			paymentDate: new Date(),
			method: 0,
			selectedTarget: props.target.options[0],
			paymentReference: '',
			currencyId: props.defaultCurrencyId,
			currencyRate: 1,
			cardToken: '',
			serviceName: '',
			otherProps: {},
			immediatelyPerformPayment: true,

			currencyCode: props.defaultCurrencyCode
		};
	}

	public componentDidMount() {
		this.loadBillingAddresses();
	}

	private async loadBillingAddresses() {
		const { accountingTarget } = this.props;
		const { selectedTarget } = this.state;

		const addresses = await this.billingService.getBillingAddresses(accountingTarget, selectedTarget.id);
		this.setState({
			selectedTargetBillingAddress: addresses[0],
			selectedTargetBillingAddresses: addresses,
		});
	}

	private isValidForm(): boolean {
		const { minAmount, maxAmount } = this.props;
		const { amount, paymentReference, paymentDate, currencyRate, method } = this.state;
		const sourceAmount = ObjectUtils.round(amount / currencyRate);

		return 0 < sourceAmount
			&& (minAmount == null || minAmount <= sourceAmount)
			&& (maxAmount == null || sourceAmount <= maxAmount)
			&& (method !== 1 || (paymentReference != null && paymentReference.trim() !== ''))
			&& paymentDate != null;
	}

	private accountingTargetParams(client: string, agent: string, supplier: string): string {
		const { accountingTarget } = this.props;
		switch (accountingTarget) {
			case 1:
				return client;
			case 2:
				return agent;
			case 4:
				return supplier;
		}

		return '';
	}

	private get targetName(): string {
		return this.accountingTargetParams('Client', 'Agent', 'Supplier');
	}
	private get targetFieldName(): string {
		return this.accountingTargetParams('clientId', 'agentId', 'supplierId');
	}

	@autobind
	private async handleOk(): Promise<void> {
		const { accountingTarget, quoteId, onSubmitPayment } = this.props;
		const {
			amount,
			paymentDate,
			method,
			selectedTarget,
			paymentReference,
			currencyId,
			currencyRate,
			otherProps,
			cardToken,
			serviceName,
			immediatelyPerformPayment,
			selectedTargetBillingAddress,
		} = this.state;

		if (selectedTargetBillingAddress != null) {
			const paymentInfo: PaymentInfo = {
				amount,
				paymentDate,
				method,
				paymentReference,
				currencyId,
				currencyRate,
				accountingTarget,
				quoteId,
				cardToken,
				serviceName,
				otherProps,
				billingAddress: {
					...selectedTargetBillingAddress,
					customerFirstName: selectedTarget.firstName,
					customerLastName: selectedTarget.lastName,
				},
				[this.targetFieldName]: selectedTarget.id
			};

			this.setState({ cardToken: '' });
			await onSubmitPayment(paymentInfo, immediatelyPerformPayment);
		}
	}

	@autobind
	private handleCancel(): void {
		this.props.onCancelDialog();
	}

	@autobind
	private handleEditAmount(newValue: number): void {
		this.setState({ amount: newValue });
	}

	@autobind
	private handleEditOtherProps(fieldName: string, newValue: any): void {
		const { otherProps } = this.state;
		this.setState({
			otherProps: {
				...otherProps,
				[fieldName]: newValue
			}
		});
	}

	@autobind
	private handleChangeTarget(selectedTarget: TargetModel): void {
		this.setState({ selectedTarget }, this.loadBillingAddresses);
	}

	@autobind
	private handleChangePaymentDate(paymentDate: Date): void {
		this.setState({ paymentDate });
	}

	@autobind
	private handleChangeMethod(value: any): void {
		this.setState({ method: value != null ? value.value : 0 });
	}

	@autobind
	private handleChangePaymentReference(paymentReference: string): void {
		this.setState({ paymentReference });
	};

	@autobind
	private async handleChangePaymentCurrencyId(paymentCurrency: CurrencySummary): Promise<void> {
		const { bookingDate, serviceDate, defaultCurrencyId, setBusy } = this.props;
		const { amount, currencyRate } = this.state;

		setBusy(true);
		try {
			let newRate = 1;
			if (defaultCurrencyId !== paymentCurrency.id) {
				const context = await this.contextService.current();
				const exchangeRateResults = await context.ExchangeRate(defaultCurrencyId, paymentCurrency.id, 1, bookingDate, serviceDate, 0).toArray();
				newRate = exchangeRateResults[exchangeRateResults.length - 1].amount;
			}

			const mergedRate = newRate / currencyRate;
			this.setState({
				currencyId: paymentCurrency.id,
				currencyRate: newRate,
				currencyCode: paymentCurrency.iso3Name,
				amount: ObjectUtils.round(amount * mergedRate, 2)
			});
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private handleChangeImmediatelyPerformPayment(immediatelyPerformPayment: boolean): void {
		this.setState({ immediatelyPerformPayment });
	}

	render(): React.ReactNode {
		const { setBusy, transactionId, editableAmount, title, target } = this.props;
		const {
			amount,
			paymentDate,
			method,
			paymentReference,
			currencyId,
			otherProps,
			immediatelyPerformPayment,
			currencyCode,
			cardToken,
			selectedTarget,
			selectedTargetBillingAddress,
			selectedTargetBillingAddresses,
		} = this.state;

		const okButton = (
			<Button
				key="ok"
				type="primary"
				onClick={this.handleOk}
				disabled={!this.isValidForm()}
			>
				Ok
			</Button>
		);

		const footer = [
			<Button
				key="cancel"
				onClick={this.handleCancel}
			>
				Cancel
			</Button>
		];

		if (method !== 2 || transactionId) {
			footer.unshift(okButton);
		}

		const options = [
			{ value: 0, label: 'Cash' },
			{ value: 1, label: 'BankTransfer' }
		];

		let creditCardItem = null;
		if (!this.props.disableCreditCardEntry) {
			options.push({ value: 2, label: 'Card' });
			if (transactionId) {
				creditCardItem = (
					<TextView
						mode="display"
						label="TransactionId"
						value={transactionId}
					/>
				);
			} else if (!cardToken) {
				creditCardItem = (
					<GatewayTabs
                        cardHolder={`${selectedTarget.firstName} ${selectedTarget.lastName}`}
						onAuthorizePayment={(t, serviceName, extra?) => {
							this.setState({ cardToken: t, serviceName, otherProps: { ...this.state.otherProps, ...extra } }, () => this.handleOk());
						}}
					/>
				);
			}
		}

		return (
			<Modal
				visible
				title={title}
				wrapClassName="vertical-center-modal"
				maskClosable={false}
				okButtonDisabled={selectedTargetBillingAddress == null}
				onOk={this.handleOk}
				onCancel={this.handleCancel}
				width={720}
				footer={footer}
			>
				<Row gutter={32}>
					<Col xs={14}>
						<Row gutter={16}>
							<Col xs={24}>
								{
									this.props.children
								}
							</Col>
							<Col xs={24}>
								{
									target.render != null &&
									target.render(selectedTarget)
								}
								<hr />
							</Col>
							<Col xs={24}>
								{
									transactionId == null &&
									<BooleanView
										label="Immediately Perform Payment?"
										value={immediatelyPerformPayment}
										onChanged={this.handleChangeImmediatelyPerformPayment}
										mode="edit"
									/>
								}
							</Col>
						</Row>
					</Col>
					<Col xs={10}>
						<Row gutter={16}>
							<Col xs={24}>
								<DropdownView
									required
									label={this.targetName}
									model={selectedTarget.id}
									options={target.options}
									keyField="id"
									textField="displayName"
									disabled={target.options.length === 1}
									onChanged={(value) => {
										if (value != null) {
											this.handleChangeTarget(value);
										}
									}}
								/>
							</Col>
							<Col xs={24}>
								{
									selectedTargetBillingAddresses && <DropdownView
										required
										label="Billing addresses"
										model={selectedTargetBillingAddress ? selectedTargetBillingAddress.address1 : undefined}
										options={selectedTargetBillingAddresses}
										keyField="address1"
										textField="address1"
										optionRenderer={(e: Address) => (
											<div>
												{e.address1}
												<br />
												{e.address2 ? <React.Fragment>{e.address2}<br /></React.Fragment> : null}
												{e.town}
												<br />
												{e.county ? <React.Fragment>{e.county}<br /></React.Fragment> : null}
												{e.postalCode}
												<br />
												{e.country}
											</div>
										)}
										disabled={selectedTargetBillingAddresses.length === 1}
										onChanged={(value) => {
											if (value != null) {
												this.setState({ selectedTargetBillingAddress: value });
											}
										}}
									/>
								}
							</Col>
							<Col xs={24}>
								<TextView
									required={method === 1}
									label="Payment Reference"
									value={paymentReference}
									onChanged={this.handleChangePaymentReference}
									mode="edit"
								/>
							</Col>
							<Col xs={24}>
								<DateView
									required
									label="Payment Date"
									value={paymentDate}
									onChanged={(value) => {
										this.handleChangePaymentDate(value);
									}}
									mode="display"
									format="small-date"
									dateUsage="utc"
								/>
							</Col>
							<Col xs={24}>
								<CurrencyEditorController
									required
									label="Currency"
									mode="edit"
									setBusy={setBusy}
									value={currencyId}
									onChange={(value) => {
										if (value != null) {
											this.handleChangePaymentCurrencyId(value);
										}
									}}
								/>
							</Col>
							<Col xs={24}>
								<DropdownView
									label="Mode"
									model={method}
									onChanged={this.handleChangeMethod}
									options={options}
								/>
							</Col>
							<Col xs={24}>
								<TextView
									label="Amount"
									value={amount}
									onChanged={this.handleEditAmount}
									mode={editableAmount ? 'edit' : 'display'}
									targetType="float+"
									suffix={currencyCode}
								/>
							</Col>
							{
								method === 2 &&
								<Col xs={24}>
									{creditCardItem}
								</Col>
							}
							<Col xs={24}>
								<TextView
									label="Description"
									value={otherProps.description}
									onChanged={(value) => {
										this.handleEditOtherProps('description', value);
									}}
									rows={2}
									mode="edit"
								/>
							</Col>
						</Row>
					</Col>
				</Row>
			</Modal>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/parts/payment-modal.tsx